import { useState } from 'react';
import { shallowEqual } from 'react-redux';
import { format } from 'date-fns';
import {
  Accordion,
  Box,
  Heading,
  Icon,
  Menu,
  Table,
  Tag,
  Text,
  TextButton,
  Toast,
  useToast,
} from '@zillow/constellation-10';
import { IconMailFilled, IconPhoneFilled } from '@zillow/constellation-icons';

import { useRentalManagerUserPropertyWebAnalyticsWebApiHandler_get } from '@zg-rentals/rental-manager-env';

import {
  ModalDeleteTenancyPeriod,
  ModalDeleteTenant,
  ModalEditTenantForm,
  ModalFormFieldLeaseTerm,
} from '@zg-rentals/modal-tenant';

import TenantDirectoryCSEvents from '../../../model/ClickstreamEvent/TenantDirectoryCSEvents';

import ProofOfInsurance from './ProofOfInsurance';

import { createZgApiHttpClient } from '../../../../client/zgApiHttpClient';

import { useSelector } from '../../../selectors/useSelector';
import accessUtils from '../../../utils/accessUtils';

import { getValueOrDash } from '../utils';

import { singleTextLineStyles } from '../../../styles';
import { columnContentMaxWidth, columnEmailBox, iconRowItem, infoCol, infoRow, sectionTitleRow } from '../styles';

import type { DatarouterError } from '@zg-rentals/rental-manager-env';

import type {
  RentalManagerRenterContactTenancyDto,
  RentalManagerRenterContactTenantDto,
  RentalManagerRenterContactTenantWithTenanciesDto,
  RentalManagerRenterUpdateRenterResponseDto,
  RenterInfoRenterAddedDto,
} from '@zg-rentals/ts-rental-manager-api-client';
import ProofOfInsuranceCSEvents, {
  PROOF_OF_INSURANCE_CS_TRIGGER_OBJECTS,
} from '../../../model/ClickstreamEvent/ProofOfInsuranceCSEvents';

type TenantAccordionItemProps = {
  tenant: RentalManagerRenterContactTenantWithTenanciesDto;
  index: number;
  onDeleteTenancyPeriodSuccess?: () => void | null;
  onDeleteTenantSuccess?: () => void | null;
  onDeleteTenancyPeriodError?: (error: DatarouterError) => void | null;
  onDeleteTenantError?: (error: DatarouterError) => void | null;
  onEditTenantSuccess?: (updatedRenterDto?: RentalManagerRenterUpdateRenterResponseDto) => void | null;
  onLeaseTermActionSuccess?: ({
    addedRenters,
    isEditMode,
  }: {
    addedRenters?: Array<RenterInfoRenterAddedDto>;
    isEditMode: boolean;
  }) => void | null;
  onEditTenantError?: (error: DatarouterError) => void | null;
  onLeaseTermActionError?: (error: DatarouterError) => void | null;
  onRequestProofSuccess?: (message: string) => void | null;
  onRequestProofError?: (message: string) => void | null;
};

const TenantAccordionItem = ({
  tenant,
  index,
  onDeleteTenancyPeriodSuccess,
  onDeleteTenancyPeriodError,
  onDeleteTenantError,
  onDeleteTenantSuccess,
  onEditTenantSuccess,
  onLeaseTermActionSuccess,
  onEditTenantError,
  onLeaseTermActionError,
  onRequestProofSuccess,
  onRequestProofError,
}: TenantAccordionItemProps) => {
  const { enqueueToast } = useToast();

  const { currentProperty, env, listingAlias, user } = useSelector(
    (state) => ({
      currentProperty: state.properties.currentProperty,
      env: state.env,
      user: state.user,
      listingAlias: state.properties.currentProperty.listingAlias,
    }),
    shallowEqual,
  );

  const appConfig = useSelector((state) => state.app.appConfig);
  const canAccessProofOfInsurance = accessUtils.canAccessProofOfInsurance({ appConfig });

  const [isModalRemoveTenantOpen, setIsModalRemoveTenantOpen] = useState(false);

  const [modalDeleteTenancyPeriodSettings, setModalDeleteTenancyPeriodSettings] = useState<{
    isOpen: boolean;
    selectedTenancyPeriod?: RentalManagerRenterContactTenancyDto | null;
  }>({
    isOpen: false,
    selectedTenancyPeriod: null,
  });

  const [modalFormFieldLeaseTermSettings, setModalFormFieldLeaseTermSettings] = useState<{
    isOpen: boolean;
    isEditMode: boolean;
    selectedTenancyPeriod?: RentalManagerRenterContactTenancyDto | null;
  }>({
    isOpen: false,
    isEditMode: true,
    selectedTenancyPeriod: null,
  });

  const [isModalEditTenantOpen, setIsModalEditTenantOpen] = useState(false);

  const { data: webAnalyticsDatWithLease } = useRentalManagerUserPropertyWebAnalyticsWebApiHandler_get(
    {
      listingAlias,
      includeLeases: true,
    },
    {
      enabled: Boolean(listingAlias),
    },
  );

  // using same client as in client/index.js
  const client = createZgApiHttpClient(env.zgApiPublic);

  const copyEmail = async () => {
    if (!tenant.emailAddress) {
      return;
    }

    try {
      await navigator.clipboard.writeText(tenant.emailAddress!);

      enqueueToast(<Toast tone="success">Email copied to clipboard</Toast>);
    } catch (err) {
      enqueueToast(<Toast tone="critical">Failed to copy email to clipboard</Toast>);
    }
  };

  const triggerCSEventEditTenantClick = () => {
    TenantDirectoryCSEvents.TenantAccordionItemEditClick({ user, currentProperty });
  };

  const triggerCSEventSendProofOfInsuranceRequestClick = () => {
    ProofOfInsuranceCSEvents.ProofOfInsuranceSendRequest({
      triggerLocation: PROOF_OF_INSURANCE_CS_TRIGGER_OBJECTS.TENANT_CARD,
      user,
      currentProperty,
    });
  };

  const triggerCSEventProofOfInsuranceRequestClick = () => {
    ProofOfInsuranceCSEvents.ProofOfInsuranceRequestClick({ user, currentProperty });
  };

  const handleEditTenantClick = () => {
    setIsModalEditTenantOpen(true);
    triggerCSEventEditTenantClick();
  };

  const handleEditTenancyPeriodClick = (tenancyPeriod?: RentalManagerRenterContactTenancyDto) => {
    setModalFormFieldLeaseTermSettings({
      isOpen: true,
      isEditMode: true,
      selectedTenancyPeriod: tenancyPeriod,
    });

    if (webAnalyticsDatWithLease) {
      const { lease, ...webAnalyticsData } = webAnalyticsDatWithLease;

      TenantDirectoryCSEvents.TenancyPeriodEditClick({
        currentProperty: webAnalyticsData ?? currentProperty,
        lease,
        user,
      });
    }
  };

  const handleDeleteTenancyPeriodClick = (tenancyPeriod?: RentalManagerRenterContactTenancyDto) => {
    setModalDeleteTenancyPeriodSettings({
      isOpen: true,
      selectedTenancyPeriod: tenancyPeriod,
    });
  };

  const handleRemoveTenantClick = () => {
    setIsModalRemoveTenantOpen(true);
  };

  const handleAddTenancyPeriodClick = () => {
    setModalFormFieldLeaseTermSettings({
      isOpen: true,
      isEditMode: false,
    });

    if (webAnalyticsDatWithLease) {
      const { lease, ...webAnalyticsData } = webAnalyticsDatWithLease;

      TenantDirectoryCSEvents.TenantAccordionItemAddTenancyPeriodClick({
        currentProperty: webAnalyticsData ?? currentProperty,
        lease,
        user,
      });
    }
  };

  const renderContactInfo = (tenant: RentalManagerRenterContactTenantWithTenanciesDto) => (
    <Box data-testid="InfoRowContact" css={infoRow}>
      <Box css={{ ...infoCol }} data-testid="InfoRowContactItem">
        <Icon role="img" size="sm" css={{ ...iconRowItem }}>
          <IconMailFilled />
        </Icon>

        {getValueOrDash(tenant.emailAddress) === '-' ? (
          '-'
        ) : (
          <Box css={columnEmailBox}>
            <Text
              className="fs-mask"
              data-testid="EmailAddress"
              color="text.neutral"
              textStyle="body"
              css={{
                ...columnContentMaxWidth,
                ...singleTextLineStyles,
              }}
            >
              {tenant.emailAddress}
            </Text>

            <TextButton size="sm" textStyle="body-sm" onClick={copyEmail}>
              Copy email
            </TextButton>
          </Box>
        )}
      </Box>

      <Box css={infoCol}>
        <Icon role="img" size="sm" css={iconRowItem}>
          <IconPhoneFilled />
        </Icon>

        <Text className="fs-mask" textStyle="body">
          {getValueOrDash(tenant.phoneNumber)}
        </Text>
      </Box>
    </Box>
  );

  const renderTenancyPeriodItemList = () => {
    return tenant.tenancies?.map((tenancyPeriod: RentalManagerRenterContactTenancyDto) => {
      return (
        <Table.Row key={tenancyPeriod.renterRoleId}>
          <Table.Cell>{format(new Date(tenancyPeriod.startDateMs), 'MM/dd/yy')}</Table.Cell>
          <Table.Cell>
            {tenancyPeriod.endDateMs ? format(new Date(tenancyPeriod.endDateMs), 'MM/dd/yy') : '-'}
          </Table.Cell>
          <Table.Cell>
            <Tag size="sm" tone="gray">
              {tenancyPeriod.state}
            </Tag>
          </Table.Cell>
          <Table.Cell>
            <Menu
              content={
                <>
                  <Menu.Item>
                    <Menu.ItemLabel
                      data-eventid={TenantDirectoryCSEvents.TenancyPeriodEditClick.data_event_id}
                      onClick={() => handleEditTenancyPeriodClick(tenancyPeriod)}
                    >
                      Edit
                    </Menu.ItemLabel>
                  </Menu.Item>
                  <Menu.Item>
                    <Menu.ItemLabel onClick={() => handleDeleteTenancyPeriodClick(tenancyPeriod)}>
                      Delete
                    </Menu.ItemLabel>
                  </Menu.Item>
                </>
              }
              onOpenChange={function UAe() {}}
              shouldAwaitInteractionResponse
            />
          </Table.Cell>
        </Table.Row>
      );
    });
  };

  const transposeFromContactWithTenanciesToRenterContactDto = (
    renter: RentalManagerRenterContactTenantWithTenanciesDto,
    renterRoleId?: string,
  ): RentalManagerRenterContactTenantDto => {
    const defaultTenancy = {
      renterRoleId: '',
      startDateMs: 0,
      state: '',
      endDateMs: 0,
    };

    // set displayTenancy based on renterRoleId
    const displayTenancy = renterRoleId
      ? (renter.tenancies?.find((tenancy) => tenancy.renterRoleId === renterRoleId) ?? defaultTenancy)
      : defaultTenancy;

    const { tenancies: _, ...renterWithoutTenancies } = renter;

    return {
      ...renterWithoutTenancies,
      displayTenancy,
    };
  };

  return (
    <>
      <Accordion.Item value={`tenant-${index}`} key={tenant.renterId}>
        <Accordion.Header>
          <Heading className="fs-mask" level={3} textStyle="body-lg-bold">
            {tenant.fullName}
          </Heading>
        </Accordion.Header>

        <Accordion.Panel>
          <Box
            data-testid="AccordionContainer"
            className="fs-unmask"
            css={{ display: 'flex', flexDirection: 'column', gap: '{spacing.600}' }}
          >
            <Box data-testid="ContactInfoContainer">
              <Box css={{ display: 'flex', justifyContent: 'space-between', ...sectionTitleRow }}>
                <Text textStyle="body-bold" color="text.neutral">
                  Contact information
                </Text>

                <TextButton
                  data-eventid={TenantDirectoryCSEvents.TenantAccordionItemEditClick.data_event_id}
                  size="sm"
                  textStyle="body-sm-bold"
                  tone="brand"
                  onImpact={false}
                  onClick={handleEditTenantClick}
                >
                  Edit
                </TextButton>
              </Box>

              <Box
                data-testid="InfoContentContainer"
                css={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '{spacing.400}' }}
              >
                {renderContactInfo(tenant)}
              </Box>
            </Box>

            {tenant.tenancies && (
              <Box data-testid="TenancyPeriodContainer">
                <Box css={{ display: 'flex', justifyContent: 'space-between', ...sectionTitleRow }}>
                  <Text textStyle="body-bold" color="text.neutral">
                    Tenancy period
                  </Text>
                  <TextButton
                    data-eventid={TenantDirectoryCSEvents.TenantAccordionItemAddTenancyPeriodClick.data_event_id}
                    size="sm"
                    textStyle="body-sm-bold"
                    tone="brand"
                    onImpact={false}
                    onClick={handleAddTenancyPeriodClick}
                  >
                    Add
                  </TextButton>
                </Box>

                <Box
                  data-testid="TenancyPeriodContentContainer"
                  css={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '{spacing.400}' }}
                >
                  <Table.Responsive
                    data-testid="TenancyPeriodTable"
                    css={{
                      width: '100%',
                      border: '1px solid',
                      borderColor: '{colors.borderLight}',
                      borderRadius: '{radii.md}',
                    }}
                  >
                    <Table.Root
                      alignments={['start', 'start', 'start', 'start']}
                      appearance="horizontal"
                      aria-label="Tenancy period table"
                      onSortChange={function UAe() {}}
                      size="md"
                    >
                      <Table.Header>
                        <Table.Row>
                          <Table.HeaderCell>Start date</Table.HeaderCell>
                          <Table.HeaderCell>End date</Table.HeaderCell>
                          <Table.HeaderCell>Status</Table.HeaderCell>
                          {/* overflow menu column */}
                          <Table.HeaderCell>&nbsp;</Table.HeaderCell>
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>{renderTenancyPeriodItemList()}</Table.Body>
                    </Table.Root>
                  </Table.Responsive>

                  <TextButton
                    css={{ alignSelf: 'flex-end', marginTop: '{spacing.200}' }}
                    size="md"
                    tone="brand"
                    // TODO: add remove tenant endpoint to remove all tenancies once complete
                    onClick={handleRemoveTenantClick}
                  >
                    Remove tenant
                  </TextButton>
                </Box>
              </Box>
            )}

            {canAccessProofOfInsurance && tenant.proofOfInsuranceStatus && (
              <Box data-testid="ProofOfInsuranceContainer">
                <Box css={{ display: 'flex', justifyContent: 'space-between', ...sectionTitleRow }}>
                  <Text textStyle="body-bold" color="text.neutral">
                    Other information
                  </Text>
                </Box>

                <Box
                  data-testid="ProofOfInsuranceContentContainer"
                  css={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '{spacing.400}' }}
                >
                  <ProofOfInsurance
                    tenant={transposeFromContactWithTenanciesToRenterContactDto(tenant)}
                    onRequestClick={triggerCSEventProofOfInsuranceRequestClick}
                    onSendRequestClick={triggerCSEventSendProofOfInsuranceRequestClick}
                    onSuccess={onRequestProofSuccess}
                    onError={onRequestProofError}
                  />
                </Box>
              </Box>
            )}
          </Box>
        </Accordion.Panel>
      </Accordion.Item>

      {/* check isOpen to pass correct renter data */}
      {modalFormFieldLeaseTermSettings.isOpen && (
        <ModalFormFieldLeaseTerm
          client={client}
          isOpen={modalFormFieldLeaseTermSettings.isOpen}
          isEditMode={modalFormFieldLeaseTermSettings.isEditMode}
          listingAlias={listingAlias}
          onClose={() => {
            setModalFormFieldLeaseTermSettings({
              isOpen: false,
              isEditMode: true,
              selectedTenancyPeriod: null,
            });
          }}
          renter={
            modalFormFieldLeaseTermSettings.isEditMode
              ? transposeFromContactWithTenanciesToRenterContactDto(
                  tenant,
                  modalFormFieldLeaseTermSettings.selectedTenancyPeriod?.renterRoleId,
                )
              : transposeFromContactWithTenanciesToRenterContactDto(tenant)
          }
          onSuccess={onLeaseTermActionSuccess}
          onError={onLeaseTermActionError}
        />
      )}

      <ModalEditTenantForm
        client={client}
        isOpen={isModalEditTenantOpen}
        listingAlias={listingAlias}
        onClose={() => {
          setIsModalEditTenantOpen(false);
        }}
        renter={transposeFromContactWithTenanciesToRenterContactDto(tenant)}
        onSuccess={onEditTenantSuccess}
        onError={onEditTenantError}
      />

      {/* check isOpen to pass correct renter data */}
      {modalDeleteTenancyPeriodSettings.isOpen && (
        <ModalDeleteTenancyPeriod
          client={client}
          isOpen={modalDeleteTenancyPeriodSettings.isOpen}
          onClose={() => setModalDeleteTenancyPeriodSettings({ isOpen: false, selectedTenancyPeriod: null })}
          renter={transposeFromContactWithTenanciesToRenterContactDto(
            tenant,
            modalDeleteTenancyPeriodSettings.selectedTenancyPeriod?.renterRoleId,
          )}
          onSuccess={onDeleteTenancyPeriodSuccess}
          onError={onDeleteTenancyPeriodError}
        />
      )}

      {isModalRemoveTenantOpen && (
        <ModalDeleteTenant
          client={client}
          isOpen={isModalRemoveTenantOpen}
          onClose={() => setIsModalRemoveTenantOpen(false)}
          renterName={tenant.fullName ?? ''}
          renterRoleIdsToDelete={tenant.tenancies?.map((tenancy) => tenancy.renterRoleId) ?? []}
          onSuccess={onDeleteTenantSuccess}
          onError={onDeleteTenantError}
        />
      )}
    </>
  );
};

export default TenantAccordionItem;
